import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';

import { TransactionNames } from './constants';
import { retryOnCondition } from './retryOnCondition';
import { runInTransaction } from './runInTransaction';

export enum RequestStatus {
  DEFAULT = 'DEFAULT',
  LOADING = 'LOADING',
  RESOLVED = 'RESOLVED',
  FAILED = 'FAILED',
}

type WrapRequestOptions = {
  errorFieldName?: string;
  transactionName?: TransactionNames;
};

type WrapRequestFunc = <T, K extends unknown[]>(
  flowAPI: ControllerFlowAPI,
  method: (flowApi: ControllerFlowAPI, ...params: K) => Promise<T>,
  dataFieldName: string | null,
  statusFieldName: string,
  errorFieldNameOrOptions?: string | WrapRequestOptions,
  retryCondition?: (data: T) => boolean,
  maxAttempts?: number,
  delayMillis?: number,
) => (...params: K) => Promise<T | undefined>;

export const wrapRequest: WrapRequestFunc =
  (
    flowAPI,
    method,
    dataFieldName,
    statusFieldName,
    errorFieldNameOrOptions,
    retryCondition,
    maxAttempts = 3,
    delayMillis = 5000,
  ) =>
  async (...params) => {
    const setProps = flowAPI.controllerConfig.setProps;
    const { getResolvedError } = flowAPI.errorHandler;
    const options: WrapRequestOptions =
      typeof errorFieldNameOrOptions === 'string'
        ? { errorFieldName: errorFieldNameOrOptions }
        : errorFieldNameOrOptions ?? {};
    const { errorFieldName, transactionName } = options;

    try {
      setProps({
        [statusFieldName]: RequestStatus.LOADING,
      });

      const fetchDataWithRetry = async () => {
        if (retryCondition) {
          return retryOnCondition({
            fetchData: () => method(flowAPI, ...params),
            checkCondition: retryCondition,
            maxAttempts,
            delayMillis,
          });
        } else {
          return method(flowAPI, ...params);
        }
      };

      const data = await (transactionName
        ? runInTransaction(flowAPI, transactionName, fetchDataWithRetry)
        : fetchDataWithRetry());

      setProps({
        [statusFieldName]: RequestStatus.RESOLVED,
        ...(dataFieldName
          ? { [dataFieldName]: data }
          : (data as Record<string, unknown>)),
        ...(errorFieldName ? { [errorFieldName]: undefined } : {}),
      });

      return data;
    } catch (error) {
      flowAPI.panoramaClient.errorMonitor().reportError(error as Error);
      const resolvedError = getResolvedError(error);

      setProps({
        [statusFieldName]: RequestStatus.FAILED,
        ...(errorFieldName
          ? {
              [errorFieldName]: resolvedError,
            }
          : {}),
      });
    }
  };
